import React, { useEffect, useState, useCallback } from 'react';
import {
  saveByocConfig, getByocConfig, removeByocConfig,
  saveByocHealthCheck,
} from '../firebase/byoc';
import { useAuth } from '../context/AuthContext';

// ── Service endpoint definitions ─────────────────────────────────────────────

const BYOC_SERVICES = [
  { id: 'mattergen', label: 'MatterGen',  desc: 'Crystal structure generation' },
  { id: 'fm4m',      label: 'FM4M (IBM)', desc: 'Polymer & molecular generation, representations' },
  { id: 'dft',       label: 'DFT (GPAW)', desc: 'Single point, DOS, band structure, relaxation' },
  { id: 'gt4sd',     label: 'GT4SD',      desc: 'Small-molecule & polymer generation' },
];

const GCP_REGIONS = ['us-central1','us-east1','us-west1','europe-west1','europe-west4','asia-east1','asia-southeast1'];

// ── Shared mini-styles ────────────────────────────────────────────────────────

const inputStyle = {
  background: '#060d18', border: '1px solid #1e3a5f', borderRadius: 6,
  color: '#e2e8f0', padding: '8px 12px', fontSize: 13, outline: 'none', width: '100%',
};
const labelStyle = { color: '#94a3b8', fontSize: 12, marginBottom: 4, display: 'block' };
const providerBtn = (active, color, disabled) => ({
  flex: 1, padding: '10px 0', borderRadius: 8, fontWeight: 600, fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
  background: active ? `${color}18` : 'transparent',
  color: active ? color : '#475569',
  border: `1px solid ${active ? color : '#1e3a5f'}`,
  opacity: disabled ? 0.4 : 1,
  position: 'relative',
});

// ── Status dot ────────────────────────────────────────────────────────────────

function StatusDot({ status }) {
  const colors = { ok: '#4ade80', error: '#f87171', unknown: '#334155', checking: '#f59e0b' };
  return (
    <span style={{
      display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
      background: colors[status] || colors.unknown,
      marginRight: 6, flexShrink: 0,
    }} />
  );
}

// ── Endpoint row with health check + autodeploy ───────────────────────────────

function EndpointRow({ svc, url, onChange, onTest, onDeploy, healthStatus, healthLatency, deployState, canDeploy }) {
  const deploying = deployState?.status === 'deploying' || deployState?.status === 'polling';
  const deployColor =
    deployState?.status === 'error' ? '#f87171' :
    deployState?.status === 'success' ? '#4ade80' :
    deploying ? '#f59e0b' : '#a78bfa';

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
      <div style={{ flex: 1 }}>
        <label style={labelStyle}>
          <StatusDot status={healthStatus} />
          {svc.label}
          <span style={{ color: '#475569', fontWeight: 400, marginLeft: 6 }}>{svc.desc}</span>
          {healthStatus === 'ok' && healthLatency != null && (
            <span style={{ color: '#4ade80', fontSize: 11, marginLeft: 8 }}>{healthLatency}ms</span>
          )}
          {deployState?.message && (
            <span style={{ color: deployColor, fontSize: 11, marginLeft: 8 }}>
              {deployState.message}
            </span>
          )}
        </label>
        <input
          value={url}
          onChange={e => onChange(svc.id, e.target.value)}
          placeholder={`https://${svc.id}-abc123-uc.a.run.app`}
          style={inputStyle}
        />
      </div>
      <button
        onClick={() => onDeploy(svc.id)}
        disabled={!canDeploy || deploying}
        title={canDeploy ? `Deploy ${svc.label} to your GCP project` : 'Enter Project ID and Region first'}
        style={{
          background: '#1a0f2e', border: `1px solid ${canDeploy && !deploying ? '#7c3aed' : '#1e3a5f'}`, borderRadius: 6,
          color: canDeploy && !deploying ? '#a78bfa' : '#334155', fontSize: 12, fontWeight: 600,
          padding: '8px 14px', cursor: canDeploy && !deploying ? 'pointer' : 'not-allowed',
          whiteSpace: 'nowrap', marginBottom: 0,
        }}
      >
        {deploying ? '⏳ Deploying' : 'Deploy'}
      </button>
      <button
        onClick={() => onTest(svc.id)}
        disabled={!url.trim()}
        style={{
          background: '#0a1120', border: '1px solid #1e3a5f', borderRadius: 6,
          color: url.trim() ? '#38bdf8' : '#334155', fontSize: 12, fontWeight: 600,
          padding: '8px 14px', cursor: url.trim() ? 'pointer' : 'not-allowed',
          whiteSpace: 'nowrap', marginBottom: 0,
        }}
      >
        Test
      </button>
    </div>
  );
}

// ── GCP config form (primary) ─────────────────────────────────────────────────

function GCPForm({ orgId, onSaved }) {
  const { user } = useAuth() || {};
  const [cfg, setCfg] = useState({
    projectId: '', serviceAccountEmail: '', region: 'us-central1',
    endpoints: { mattergen: '', fm4m: '', dft: '', gt4sd: '' },
  });
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [msg, setMsg] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [health, setHealth] = useState({}); // { mattergen: { status, latency }, ... }
  const [deploys, setDeploys] = useState({}); // { mattergen: { status, message, operation }, ... }

  const load = useCallback(async () => {
    const existing = await getByocConfig(orgId, 'gcp');
    if (existing) {
      setCfg({
        projectId: existing.projectId || '',
        serviceAccountEmail: existing.serviceAccountEmail || '',
        region: existing.region || 'us-central1',
        endpoints: {
          mattergen: existing.endpoints?.mattergen || '',
          fm4m: existing.endpoints?.fm4m || '',
          dft: existing.endpoints?.dft || '',
          gt4sd: existing.endpoints?.gt4sd || '',
        },
      });
      // Restore last health check results
      if (existing.lastHealthCheck?.results) {
        const restored = {};
        for (const [k, v] of Object.entries(existing.lastHealthCheck.results)) {
          restored[k] = { status: v ? 'ok' : 'error', latency: null };
        }
        setHealth(restored);
      }
    }
    setLoaded(true);
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const set = k => e => setCfg(c => ({ ...c, [k]: e.target.value }));
  const setEndpoint = (id, val) => setCfg(c => ({ ...c, endpoints: { ...c.endpoints, [id]: val } }));

  const proxyBase = process.env.REACT_APP_GPT_API
    ? process.env.REACT_APP_GPT_API.replace(/\/api\/chat$/, '')
    : 'http://localhost:8020';

  const testEndpoint = async (serviceId) => {
    const url = cfg.endpoints[serviceId];
    if (!url?.trim()) return;
    setHealth(h => ({ ...h, [serviceId]: { status: 'checking', latency: null } }));
    try {
      const token = user ? await user.getIdToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const resp = await fetch(`${proxyBase}/byoc/health`, {
        method: 'POST', headers, body: JSON.stringify({ url }),
      });
      const data = await resp.json();
      setHealth(h => ({ ...h, [serviceId]: { status: data.ok ? 'ok' : 'error', latency: data.latency || null } }));
    } catch (err) {
      setHealth(h => ({ ...h, [serviceId]: { status: 'error', latency: null } }));
    }
  };

  const deployEndpoint = async (serviceId) => {
    if (!cfg.projectId.trim() || !cfg.region) {
      setDeploys(d => ({ ...d, [serviceId]: { status: 'error', message: 'Project ID and Region required' } }));
      return;
    }

    setDeploys(d => ({ ...d, [serviceId]: { status: 'deploying', message: 'Submitting...' } }));

    try {
      const token = user ? await user.getIdToken() : null;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const resp = await fetch(`${proxyBase}/byoc/deploy`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          service: serviceId,
          projectId: cfg.projectId.trim(),
          region: cfg.region,
          serviceAccountEmail: cfg.serviceAccountEmail.trim() || undefined,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.ok) {
        setDeploys(d => ({ ...d, [serviceId]: { status: 'error', message: data.error || `HTTP ${resp.status}` } }));
        return;
      }

      setDeploys(d => ({ ...d, [serviceId]: { status: 'polling', message: 'Provisioning...', operation: data.operation } }));

      // Poll the operation until done (up to ~10 min)
      const startedAt = Date.now();
      const pollInterval = 5000;
      const timeoutMs = 10 * 60 * 1000;

      while (Date.now() - startedAt < timeoutMs) {
        await new Promise(r => setTimeout(r, pollInterval));
        const elapsed = Math.floor((Date.now() - startedAt) / 1000);
        setDeploys(d => ({ ...d, [serviceId]: { ...d[serviceId], status: 'polling', message: `Provisioning... ${elapsed}s` } }));

        const statusHeaders = { 'Content-Type': 'application/json' };
        if (token) statusHeaders['Authorization'] = `Bearer ${token}`;
        const statusResp = await fetch(`${proxyBase}/byoc/deploy/status?operation=${encodeURIComponent(data.operation)}`, {
          headers: statusHeaders,
        });
        const statusData = await statusResp.json();

        if (statusData.done) {
          if (statusData.ok && statusData.url) {
            setDeploys(d => ({ ...d, [serviceId]: { status: 'success', message: 'Deployed ✓' } }));
            setCfg(c => ({ ...c, endpoints: { ...c.endpoints, [serviceId]: statusData.url } }));
            // Auto health check after deploy succeeds
            setTimeout(() => testEndpoint(serviceId), 1500);
          } else {
            setDeploys(d => ({ ...d, [serviceId]: { status: 'error', message: statusData.error || 'Deploy failed' } }));
          }
          return;
        }
      }

      setDeploys(d => ({ ...d, [serviceId]: { status: 'error', message: 'Timed out waiting for deployment' } }));
    } catch (err) {
      setDeploys(d => ({ ...d, [serviceId]: { status: 'error', message: err.message } }));
    }
  };

  const testAll = async () => {
    const promises = BYOC_SERVICES
      .filter(s => cfg.endpoints[s.id]?.trim())
      .map(s => testEndpoint(s.id));
    await Promise.all(promises);
  };

  const handleSave = async () => {
    if (!cfg.projectId.trim()) { setMsg('Project ID is required.'); return; }
    const hasEndpoint = Object.values(cfg.endpoints).some(v => v?.trim());
    if (!hasEndpoint) { setMsg('Enter at least one endpoint URL.'); return; }
    setSaving(true); setMsg('');
    try {
      await saveByocConfig(orgId, 'gcp', { ...cfg, enabled: true });
      // Persist health check results
      const healthResults = {};
      for (const [k, v] of Object.entries(health)) healthResults[k] = v.status === 'ok';
      await saveByocHealthCheck(orgId, healthResults);
      setMsg('Saved.'); onSaved?.();
    } catch { setMsg('Failed to save.'); }
    finally { setSaving(false); }
  };

  const handleRemove = async () => {
    setRemoving(true);
    await removeByocConfig(orgId, 'gcp');
    setCfg({ projectId: '', serviceAccountEmail: '', region: 'us-central1', endpoints: { mattergen: '', fm4m: '', dft: '', gt4sd: '' } });
    setHealth({});
    setMsg('Configuration removed.');
    setRemoving(false); onSaved?.();
  };

  if (!loaded) return <p style={{ color: '#475569', fontSize: 13 }}>Loading...</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Setup instructions */}
      <div style={{ background: '#0a1120', borderRadius: 8, padding: 16 }}>
        <p style={{ color: '#4285f4', fontWeight: 600, fontSize: 13, marginBottom: 8 }}>How it works</p>
        <ol style={{ color: '#475569', fontSize: 12, paddingLeft: 18, margin: 0, lineHeight: 1.8 }}>
          <li>Enter your GCP <strong style={{ color: '#94a3b8' }}>Project ID</strong> and pick a region</li>
          <li>Grant CrimsonScan's control-plane SA <code style={{ color: '#a78bfa' }}>roles/run.admin</code> on your project (one-time)</li>
          <li>Click <strong style={{ color: '#a78bfa' }}>Deploy</strong> per service — CrimsonScan provisions Cloud Run in your project using CrimsonScan-published containers</li>
          <li>Endpoint URL auto-fills and we run a health check. Your data never leaves your project — jobs flow through a secure relay.</li>
        </ol>
      </div>

      {/* Project-level config */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle}>Project ID *</label>
          <input value={cfg.projectId} onChange={set('projectId')} placeholder="my-gcp-project" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Service Account Email</label>
          <input value={cfg.serviceAccountEmail} onChange={set('serviceAccountEmail')} placeholder="runner@my-project.iam.gserviceaccount.com" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Region</label>
          <select value={cfg.region} onChange={set('region')} style={{ ...inputStyle, cursor: 'pointer' }}>
            {GCP_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      {/* Endpoint URLs */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <label style={{ ...labelStyle, marginBottom: 0, fontWeight: 600 }}>Cloud Run Endpoint URLs</label>
          <button
            onClick={testAll}
            disabled={!Object.values(cfg.endpoints).some(v => v?.trim())}
            style={{
              background: 'none', border: '1px solid #1e3a5f', borderRadius: 6,
              color: '#38bdf8', fontSize: 11, fontWeight: 600, padding: '4px 10px',
              cursor: 'pointer',
            }}
          >
            Test All
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {BYOC_SERVICES.map(svc => (
            <EndpointRow
              key={svc.id}
              svc={svc}
              url={cfg.endpoints[svc.id]}
              onChange={setEndpoint}
              onTest={testEndpoint}
              onDeploy={deployEndpoint}
              healthStatus={health[svc.id]?.status || 'unknown'}
              healthLatency={health[svc.id]?.latency}
              deployState={deploys[svc.id]}
              canDeploy={!!(cfg.projectId.trim() && cfg.region)}
            />
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={handleSave} disabled={saving} style={{
          background: '#4285f422', border: '1px solid #4285f4', borderRadius: 6,
          color: '#4285f4', fontWeight: 600, fontSize: 13, padding: '7px 18px',
          cursor: 'pointer', opacity: saving ? 0.5 : 1,
        }}>
          {saving ? 'Saving...' : 'Save GCP Config'}
        </button>
        <button onClick={handleRemove} disabled={removing} style={{
          background: 'none', border: '1px solid #3a1a1a', borderRadius: 6,
          color: '#f87171', fontSize: 12, padding: '7px 14px',
          cursor: 'pointer', opacity: removing ? 0.5 : 1,
        }}>
          Remove
        </button>
        {msg && <span style={{ color: msg === 'Saved.' ? '#4ade80' : '#f87171', fontSize: 12 }}>{msg}</span>}
      </div>
    </div>
  );
}

// ── Main BYOC panel ───────────────────────────────────────────────────────────

export default function ByocPanel({ orgId }) {
  const [active, setActive] = useState(null); // 'gcp' | 'aws' | 'azure'
  const [configs, setConfigs] = useState({ aws: null, gcp: null, azure: null });
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    if (!orgId) return;
    const { getAllByocConfigs } = await import('../firebase/byoc');
    setConfigs(await getAllByocConfigs(orgId));
    setLoading(false);
  }, [orgId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const PROVIDERS = [
    { id: 'gcp',   label: 'Google Cloud',         color: '#4285f4', enabled: true },
    { id: 'aws',   label: 'Amazon Web Services',  color: '#f59e0b', enabled: false },
    { id: 'azure', label: 'Microsoft Azure',       color: '#0078d4', enabled: false },
  ];

  return (
    <div>
      <p style={{ color: '#a78bfa', fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
        Bring Your Own Compute (BYOC)
      </p>
      <p style={{ color: '#475569', fontSize: 13, marginBottom: 16 }}>
        Run generation and DFT jobs on your own infrastructure. CrimsonScan deploys
        containerized models to your cloud — your data stays in your environment.
      </p>

      {/* Provider selector */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {PROVIDERS.map(p => (
          <button
            key={p.id}
            onClick={() => p.enabled && setActive(active === p.id ? null : p.id)}
            disabled={!p.enabled}
            style={providerBtn(active === p.id, p.color, !p.enabled)}
          >
            <StatusDot status={configs[p.id]?.enabled ? 'ok' : 'unknown'} />
            {p.label}
            {!p.enabled && (
              <span style={{
                position: 'absolute', top: -8, right: -4, fontSize: 9, fontWeight: 700,
                background: '#1e293b', color: '#475569', padding: '1px 6px', borderRadius: 4,
                border: '1px solid #334155',
              }}>
                Coming soon
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Active provider form — only GCP for now */}
      {!loading && active === 'gcp' && <GCPForm orgId={orgId} onSaved={loadAll} />}

      {/* AWS/Azure coming soon message */}
      {!loading && (active === 'aws' || active === 'azure') && (
        <div style={{ background: '#0a1120', borderRadius: 8, padding: 20, textAlign: 'center' }}>
          <p style={{ color: '#475569', fontSize: 13, margin: 0 }}>
            {active === 'aws' ? 'AWS' : 'Azure'} support is coming soon.
            Contact us if you need it prioritized.
          </p>
        </div>
      )}

      {/* Summary of configured providers */}
      {!loading && !active && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PROVIDERS.map(p => configs[p.id]?.enabled && (
            <div key={p.id} style={{
              background: '#060d18', border: `1px solid ${p.color}44`, borderRadius: 6,
              padding: '8px 14px', fontSize: 12,
            }}>
              <StatusDot status="ok" />
              <span style={{ color: p.color, fontWeight: 600 }}>{p.label}</span>
              <span style={{ color: '#475569', marginLeft: 8 }}>
                {configs[p.id].region}
                {configs[p.id].endpoints && ` · ${Object.values(configs[p.id].endpoints).filter(v => v?.trim()).length} endpoints`}
              </span>
            </div>
          ))}
          {PROVIDERS.every(p => !configs[p.id]?.enabled) && (
            <p style={{ color: '#334155', fontSize: 13, margin: 0 }}>
              No providers configured. Select Google Cloud above to get started.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
